/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { geoEqualEarth, geoMercator, geoPath } from "d3-geo";
import commonNameTagIcon from "../assets/common-name-tag.png";
import scientificNameTagIcon from "../assets/scientific-name-tag.png";
import { feature } from "topojson-client";
import countries110m from "world-atlas/countries-110m.json";
import galleryFrame from "../assets/galleryFrame.png";
import ProfileSectionAction from "./ProfileSectionAction";

function getDefaultForm(forms) {
  if (!Array.isArray(forms) || forms.length === 0) return null;
  return forms.find((f) => f?.isDefault) || forms[0];
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WORLD_COUNTRIES = feature(
  countries110m,
  countries110m.objects.countries,
).features;

function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function cleanLocationPart(value) {
  return String(value || "").trim();
}

function normalizeLocationPart(value) {
  const normalized = cleanLocationPart(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\bprovincia de\b/g, "")
    .replace(/\bprovince\b/g, "")
    .replace(/\bdepartamento de\b/g, "")
    .replace(/\bdepartment\b/g, "")
    .replace(/\bdepartamento\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (normalized === "ciudad de mexico" || normalized === "mexico city") {
    return "ciudad de mexico";
  }

  return normalized;
}

function displayLocationPart(value) {
  return cleanLocationPart(value);
}

function sameLocationPart(a, b) {
  return normalizeLocationPart(a) === normalizeLocationPart(b);
}

function getPhotoLocationData(form) {
  const lat = asNumber(form?.locationLat);
  const lng = asNumber(form?.locationLng);

  if (lat === null || lng === null) return null;

  const country =
    displayLocationPart(form?.locationCountry) || "Unknown country";
  const city = displayLocationPart(form?.locationCity);
  const rawAdminArea = displayLocationPart(form?.locationAdminArea);

  const adminArea =
    sameLocationPart(country, "Colombia") && sameLocationPart(city, "Bogotá")
      ? "Bogotá"
      : rawAdminArea || "Unknown state/department";

  return {
    lat,
    lng,
    country,
    adminArea,
    city,
    name: getPhotoLocationLabel(form) || "Selected location",
  };
}

function LocationWorldMap({ points, selectedCountry }) {
  const width = 320;
  const height = 180;

  const selectedCountryFeature = WORLD_COUNTRIES.find(
    (country) =>
      String(country?.properties?.name || "").toLowerCase() ===
      String(selectedCountry || "").toLowerCase(),
  );

  const projection = (selectedCountryFeature ? geoMercator() : geoEqualEarth())
    .fitSize([width, height], selectedCountryFeature || { type: "Sphere" })
    .clipAngle(null);

  const path = geoPath(projection);
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full rounded-[1.25rem] border border-[#93ca87]/40 bg-[#f4f6f8]"
      role="img"
      aria-label="Map of photo locations"
    >
      <rect width={width} height={height} fill="#EAF7F6" />{" "}
      {WORLD_COUNTRIES.map((country, index) => {
        const countryName = country?.properties?.name || "";
        const isSelected = sameLocationPart(countryName, selectedCountry);
        const hasPhotos = points.some((point) =>
          sameLocationPart(point.country, countryName),
        );

        return (
          <path
            key={index}
            d={path(country)}
            fill={isSelected ? "#24C3BE" : hasPhotos ? "#CFF4EF" : "#FCFFF6"}
            fillOpacity={isSelected ? "0.36" : "1"}
            stroke={isSelected ? "#24C3BE" : "#374049"}
            strokeOpacity={isSelected ? "0.95" : "0.35"}
            strokeWidth={isSelected ? "0.9" : "0.45"}
          />
        );
      })}
      {points.map((point, index) => {
        const projected = projection([point.lng, point.lat]);
        if (!projected) return null;

        return (
          <circle
            key={`${point.lat}-${point.lng}-${index}`}
            cx={projected[0]}
            cy={projected[1]}
            r="1.5"
            strokeWidth="0.4"
            fill="#24c3be"
            stroke="#374049"
          />
        );
      })}
    </svg>
  );
}

function parseDateKey(value) {
  const parts = String(value || "").split("-");
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

function getPhotoDateValue(form) {
  const date = form?.date;

  if (date && typeof date === "object") {
    const day = date.day || "NA";
    const month = date.month || "NA";
    const year = date.year || "NA";

    if (day === "NA" || month === "NA" || year === "NA") return "";

    const key = `${year}-${month}-${day}`;
    return parseDateKey(key) ? key : "";
  }

  if (typeof date === "string") return date;

  return form?.photoDate || form?.createdAt || form?.uploadedAt || "";
}

function getPhotoLocationLabel(form) {
  return (
    form?.locationName ||
    form?.location ||
    [form?.locationCity, form?.locationAdminArea, form?.locationCountry]
      .filter(Boolean)
      .join(", ")
  );
}

export default function ProfileFavorites({
  isOwnProfile,
  profile,
  savedMessage,
  openFavoriteViewer,
  openFavoriteSelector,
  favSelectorOpen,
  setFavSelectorOpen,
  selectedSpeciesForFav,
  setSelectedSpeciesForFav,
  speciesWithPhotos,
  assignFavorite,
  pickerOnly = false,
  forumPhotoPickerMode = false,
  collectionPickerMode = false,
  collectionName = "",
  collectionPhotos = [],
  selectedCollectionPhotos = [],
  onToggleCollectionPhoto,
  onAddCollectionPhotos,
  addingCollectionPhotos = false,
}) {
  const [selectorLetter, setSelectorLetter] = useState("A");
  const [selectorSort, setSelectorSort] = useState("scientific");
  const [selectorMode, setSelectorMode] = useState("alpha");
  const [selectedFavoriteDate, setSelectedFavoriteDate] = useState("");
  const [calendarYear, setCalendarYear] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(null);
  const [showNoDatePhotos, setShowNoDatePhotos] = useState(false);
  const [selectedFavoriteCountry, setSelectedFavoriteCountry] = useState("");
  const [selectedFavoriteAdminArea, setSelectedFavoriteAdminArea] =
    useState("");
  const [previewPhotoBySpecies, setPreviewPhotoBySpecies] = useState({});
  const [previewSlideBySpecies, setPreviewSlideBySpecies] = useState({});
  const [pendingFavoriteUrl, setPendingFavoriteUrl] = useState(null);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const [editingHighlights, setEditingHighlights] = useState(false);
  const [showGalleryHint, setShowGalleryHint] = useState(false);

  const existingCollectionPhotoUrls = useMemo(
    () => new Set(collectionPhotos.map((photo) => photo?.url).filter(Boolean)),
    [collectionPhotos],
  );

  const selectedCollectionPhotoUrls = useMemo(
    () =>
      new Set(
        selectedCollectionPhotos.map((item) => item?.form?.url).filter(Boolean),
      ),
    [selectedCollectionPhotos],
  );

  function isPhotoAlreadyInCollection(url) {
    return !!url && existingCollectionPhotoUrls.has(url);
  }

  function isCollectionPhotoSelected(url) {
    return !!url && selectedCollectionPhotoUrls.has(url);
  }

  const availableFavoriteDates = useMemo(() => {
    const dates = new Set();

    speciesWithPhotos.forEach((sp) => {
      const forms = Array.isArray(sp.forms) ? sp.forms : [];
      forms.forEach((form) => {
        const value = getPhotoDateValue(form);
        if (value) dates.add(value);
      });
    });

    return [...dates].sort((a, b) => new Date(a) - new Date(b));
  }, [speciesWithPhotos]);

  const availableFavoriteYears = useMemo(() => {
    return [
      ...new Set(
        availableFavoriteDates
          .map((date) => parseDateKey(date)?.getFullYear())
          .filter(Boolean),
      ),
    ];
  }, [availableFavoriteDates]);

  const availableFavoriteMonthsByYear = useMemo(() => {
    const grouped = {};

    availableFavoriteDates.forEach((dateKey) => {
      const date = parseDateKey(dateKey);
      if (!date) return;

      const year = date.getFullYear();
      const month = date.getMonth();

      if (!grouped[year]) grouped[year] = [];
      if (!grouped[year].includes(month)) grouped[year].push(month);
    });

    Object.keys(grouped).forEach((year) => {
      grouped[year].sort((a, b) => a - b);
    });

    return grouped;
  }, [availableFavoriteDates]);

  const favoriteDateCounts = useMemo(() => {
    const counts = {};

    speciesWithPhotos.forEach((sp) => {
      const forms = Array.isArray(sp.forms) ? sp.forms : [];

      forms.forEach((form) => {
        const value = getPhotoDateValue(form);
        if (!value) return;
        counts[value] = (counts[value] || 0) + 1;
      });
    });

    return counts;
  }, [speciesWithPhotos]);

  const selectedDateHasPhotos = !!favoriteDateCounts[selectedFavoriteDate];

  const favoriteLocationCountries = useMemo(() => {
    const countries = new Set();

    speciesWithPhotos.forEach((sp) => {
      const forms = Array.isArray(sp.forms) ? sp.forms : [];

      forms.forEach((form) => {
        const locationData = getPhotoLocationData(form);
        if (locationData?.country) {
          const alreadyIncluded = [...countries].some((country) =>
            sameLocationPart(country, locationData.country),
          );

          if (!alreadyIncluded) countries.add(locationData.country);
        }
      });
    });

    return [...countries].sort((a, b) => a.localeCompare(b));
  }, [speciesWithPhotos]);

  const favoriteLocationAdminAreasByCountry = useMemo(() => {
    const grouped = {};

    speciesWithPhotos.forEach((sp) => {
      const forms = Array.isArray(sp.forms) ? sp.forms : [];

      forms.forEach((form) => {
        const locationData = getPhotoLocationData(form);
        if (!locationData?.country) return;

        if (!grouped[locationData.country]) grouped[locationData.country] = [];

        const alreadyIncluded = grouped[locationData.country].some(
          (adminArea) => sameLocationPart(adminArea, locationData.adminArea),
        );

        if (!alreadyIncluded) {
          grouped[locationData.country].push(locationData.adminArea);
        }
      });
    });

    Object.keys(grouped).forEach((country) => {
      grouped[country].sort((a, b) => a.localeCompare(b));
    });

    return grouped;
  }, [speciesWithPhotos]);

  const favoriteLocationPoints = useMemo(() => {
    const points = [];

    speciesWithPhotos.forEach((sp) => {
      const forms = Array.isArray(sp.forms) ? sp.forms : [];

      forms.forEach((form) => {
        const locationData = getPhotoLocationData(form);
        if (!locationData) return;

        const matchesCountry = sameLocationPart(
          locationData.country,
          selectedFavoriteCountry,
        );

        const matchesAdminArea = sameLocationPart(
          locationData.adminArea,
          selectedFavoriteAdminArea,
        );

        if (matchesCountry && matchesAdminArea) {
          points.push(locationData);
        }
      });
    });

    return points;
  }, [speciesWithPhotos, selectedFavoriteCountry, selectedFavoriteAdminArea]);

  const calendarCells = useMemo(() => {
    if (calendarYear === null || calendarMonth === null) return [];

    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const leadingBlanks = firstDay.getDay();

    const cells = [];

    for (let i = 0; i < leadingBlanks; i++) {
      cells.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarYear, calendarMonth, day);
      cells.push(toDateKey(date));
    }

    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    return cells;
  }, [calendarYear, calendarMonth]);

  function selectCalendarDate(dateKey) {
    setSelectedFavoriteDate(dateKey);
    setCalendarYear(parseDateKey(dateKey).getFullYear());
    setCalendarMonth(parseDateKey(dateKey).getMonth());
    setShowNoDatePhotos(false);
    setSelectedSpeciesForFav(null);
    setPendingFavoriteUrl(null);
  }

  function moveToAvailableDate(direction) {
    if (availableFavoriteDates.length === 0) return;

    const currentIndex = availableFavoriteDates.indexOf(selectedFavoriteDate);
    const fallbackIndex = direction > 0 ? 0 : availableFavoriteDates.length - 1;

    const nextIndex =
      currentIndex === -1
        ? fallbackIndex
        : Math.min(
            Math.max(currentIndex + direction, 0),
            availableFavoriteDates.length - 1,
          );

    selectCalendarDate(availableFavoriteDates[nextIndex]);
  }

  function jumpToYear(year) {
    const firstDateInYear = availableFavoriteDates.find((dateKey) => {
      const date = parseDateKey(dateKey);
      return date?.getFullYear() === year;
    });

    if (firstDateInYear) selectCalendarDate(firstDateInYear);
  }

  function jumpToMonth(month) {
    const firstDateInMonth = availableFavoriteDates.find((dateKey) => {
      const date = parseDateKey(dateKey);
      return date?.getFullYear() === calendarYear && date?.getMonth() === month;
    });

    if (firstDateInMonth) selectCalendarDate(firstDateInMonth);
  }

  const selectFavoriteCountry = useCallback(
    (country) => {
      const firstAdminArea =
        favoriteLocationAdminAreasByCountry[country]?.[0] || "";

      setSelectedFavoriteCountry(country);
      setSelectedFavoriteAdminArea(firstAdminArea);
      setSelectedSpeciesForFav(null);
      setPendingFavoriteUrl(null);
      setPreviewPhotoBySpecies({});
      setPreviewSlideBySpecies({});
    },
    [favoriteLocationAdminAreasByCountry, setSelectedSpeciesForFav],
  );

  useEffect(() => {
    if (selectorMode !== "date") return;
    if (showNoDatePhotos) return;

    const activeDate =
      parseDateKey(selectedFavoriteDate) ||
      parseDateKey(availableFavoriteDates[0]);

    if (!activeDate) return;

    const activeKey = toDateKey(activeDate);

    if (!selectedFavoriteDate || !favoriteDateCounts[selectedFavoriteDate]) {
      setSelectedFavoriteDate(activeKey);
    }

    setCalendarYear(activeDate.getFullYear());
    setCalendarMonth(activeDate.getMonth());
  }, [
    selectorMode,
    showNoDatePhotos,
    selectedFavoriteDate,
    availableFavoriteDates,
    favoriteDateCounts,
  ]);

  useEffect(() => {
    if (selectorMode !== "location") return;
    if (favoriteLocationCountries.length === 0) return;

    const activeCountry = favoriteLocationCountries.includes(
      selectedFavoriteCountry,
    )
      ? selectedFavoriteCountry
      : favoriteLocationCountries[0];

    if (activeCountry !== selectedFavoriteCountry) {
      selectFavoriteCountry(activeCountry);
      return;
    }

    const adminAreas = favoriteLocationAdminAreasByCountry[activeCountry] || [];

    if (
      adminAreas.length > 0 &&
      !adminAreas.includes(selectedFavoriteAdminArea)
    ) {
      setSelectedFavoriteAdminArea(adminAreas[0]);
    }
  }, [
    selectorMode,
    favoriteLocationCountries,
    favoriteLocationAdminAreasByCountry,
    selectedFavoriteCountry,
    selectedFavoriteAdminArea,
    selectFavoriteCountry,
  ]);

  const filteredSpeciesForFav = useMemo(() => {
    if (selectorMode === "date") {
      if (!showNoDatePhotos && !selectedDateHasPhotos) return [];

      return speciesWithPhotos
        .flatMap((sp) => {
          const forms = Array.isArray(sp.forms) ? sp.forms : [];

          return forms
            .filter((form) => {
              const value = getPhotoDateValue(form);
              return showNoDatePhotos ? !value : value === selectedFavoriteDate;
            })
            .map((form, photoIndex) => ({
              ...sp,
              id: `${sp.id}-date-${form.id || form.url || photoIndex}`,
              forms: [form],
            }));
        })
        .sort((a, b) => {
          const av = a.name || "";
          const bv = b.name || "";
          return av.localeCompare(bv);
        });
    }

    if (selectorMode === "location") {
      if (!selectedFavoriteCountry || !selectedFavoriteAdminArea) return [];

      return speciesWithPhotos
        .flatMap((sp) => {
          const forms = Array.isArray(sp.forms) ? sp.forms : [];

          return forms
            .filter((form) => {
              const locationData = getPhotoLocationData(form);
              if (!locationData) return false;

              const matchesCountry = sameLocationPart(
                locationData.country,
                selectedFavoriteCountry,
              );

              const matchesAdminArea = sameLocationPart(
                locationData.adminArea,
                selectedFavoriteAdminArea,
              );

              return matchesCountry && matchesAdminArea;
            })
            .map((form, photoIndex) => ({
              ...sp,
              id: `${sp.id}-location-${form.id || form.url || photoIndex}`,
              forms: [form],
            }));
        })
        .sort((a, b) => {
          const av = a.name || "";
          const bv = b.name || "";
          return av.localeCompare(bv);
        });
    }

    return [...speciesWithPhotos]
      .filter((sp) => {
        const value =
          selectorSort === "common" ? sp.common || "" : sp.name || "";
        return value.toUpperCase().startsWith(selectorLetter);
      })
      .sort((a, b) => {
        const av =
          selectorSort === "common" ? a.common || a.name || "" : a.name || "";
        const bv =
          selectorSort === "common" ? b.common || b.name || "" : b.name || "";
        return av.localeCompare(bv);
      });
  }, [
    speciesWithPhotos,
    selectorLetter,
    selectorSort,
    selectorMode,
    selectedFavoriteDate,
    selectedDateHasPhotos,
    showNoDatePhotos,
    selectedFavoriteCountry,
    selectedFavoriteAdminArea,
  ]);

  function slidePreviewPhoto(speciesId, nextIndex, direction, nextUrl) {
    if (!nextUrl) return;

    const img = new Image();
    img.src = nextUrl;

    img.onload = () => {
      setPreviewPhotoBySpecies((prev) => {
        const currentIndex = prev[speciesId] ?? 0;

        setPreviewSlideBySpecies((slidePrev) => ({
          ...slidePrev,
          [speciesId]: { from: currentIndex, to: nextIndex, direction },
        }));

        window.setTimeout(() => {
          setPreviewSlideBySpecies((slidePrev) => {
            const updated = { ...slidePrev };
            delete updated[speciesId];
            return updated;
          });
        }, 320);

        return {
          ...prev,
          [speciesId]: nextIndex,
        };
      });
    };
  }

  useEffect(() => {
    if (favSelectorOpen) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }

    const savedScrollY = sessionStorage.getItem("bioindex_gallery_scroll_y");
    if (!savedScrollY) return;

    sessionStorage.removeItem("bioindex_gallery_scroll_y");

    requestAnimationFrame(() => {
      window.scrollTo({
        top: Number(savedScrollY),
        left: 0,
        behavior: "instant",
      });
    });
  }, [favSelectorOpen]);
  if (favSelectorOpen) {
    return (
      <>
        <style>
          {`
      @keyframes slideFromRight {
        from {
          transform: translateX(100%);
        }
        to {
          transform: translateX(0);
        }
      }

      @keyframes slideFromLeft {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
      }
    `}
        </style>
        <div
          className={
            pickerOnly
              ? ""
              : "fixed inset-0 z-[9999] bg-[#374049] overflow-y-auto px-3 py-6"
          }
        >
          <div className="bg-[#f4f6f8] rounded-[2rem] p-5 sm:p-8 w-full max-w-6xl mx-auto shadow-2xl border border-white/70">
            {" "}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div>
                <h3
                  className="text-3xl text-[#374049]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                >
                  {collectionPickerMode
                    ? `Add Photos to ${collectionName}`
                    : forumPhotoPickerMode
                      ? "Select a Photo"
                      : "Select a Highlight"}
                </h3>

                <p className="text-sm text-slate-500 mt-1">
                  {collectionPickerMode
                    ? "Browse your observations and select every photo you want to add."
                    : forumPhotoPickerMode
                      ? "Browse your uploaded observations and select one photo for this topic."
                      : "Choose one photo to feature in your personal highlights."}
                </p>

                {collectionPickerMode && (
                  <p className="mt-2 text-xs font-medium text-[#01767a]">
                    {selectedCollectionPhotos.length} photo
                    {selectedCollectionPhotos.length === 1 ? "" : "s"} selected
                  </p>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {selectorMode === "alpha" && (
                  <div className="flex rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] p-1 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setSelectorSort("scientific")}
                      className={`h-8 min-w-12 rounded-full px-2 transition ${
                        selectorSort === "scientific"
                          ? "bg-[#24C3BE] text-white shadow-sm"
                          : "text-[#374049]/60 hover:bg-white hover:text-[#24C3BE]"
                      }`}
                      title="Sort by scientific name"
                    >
                      <img
                        src={scientificNameTagIcon}
                        alt=""
                        className="h-6 w-14 object-contain"
                      />
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectorSort("common")}
                      className={`h-8 min-w-12 rounded-full px-2 transition ${
                        selectorSort === "common"
                          ? "bg-[#24C3BE] text-white shadow-sm"
                          : "text-[#374049]/60 hover:bg-white hover:text-[#24C3BE]"
                      }`}
                      title="Sort by common name"
                    >
                      <img
                        src={commonNameTagIcon}
                        alt=""
                        className="h-6 w-14 object-contain"
                      />
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setSelectorMode((prev) =>
                      prev === "date" ? "alpha" : "date",
                    );
                    setSelectedSpeciesForFav(null);
                    setPendingFavoriteUrl(null);
                    setShowNoDatePhotos(false);
                  }}
                  className={`h-10 w-10 rounded-full border text-sm shadow-sm transition flex items-center justify-center ${
                    selectorMode === "date"
                      ? "bg-[#24c3be] border-[#24c3be] text-white"
                      : "bg-white border-[#93ca87]/40 text-[#374049] hover:bg-[#24c3be]/10 hover:text-[#24c3be]"
                  }`}
                  title="Browse by date"
                >
                  📅
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setSelectorMode((prev) =>
                      prev === "location" ? "alpha" : "location",
                    );
                    setSelectedSpeciesForFav(null);
                    setPendingFavoriteUrl(null);
                    setShowNoDatePhotos(false);
                  }}
                  className={`h-10 w-10 rounded-full border text-sm shadow-sm transition flex items-center justify-center ${
                    selectorMode === "location"
                      ? "bg-[#24c3be] border-[#24c3be] text-white"
                      : "bg-white border-[#93ca87]/40 text-[#374049] hover:bg-[#24c3be]/10 hover:text-[#24c3be]"
                  }`}
                  title="Browse by location"
                >
                  🌐
                </button>

                {collectionPickerMode && (
                  <button
                    type="button"
                    disabled={
                      selectedCollectionPhotos.length === 0 ||
                      addingCollectionPhotos
                    }
                    onClick={onAddCollectionPhotos}
                    className={`rounded-full px-4 py-2 text-xs font-medium text-white shadow-sm transition ${
                      selectedCollectionPhotos.length === 0 ||
                      addingCollectionPhotos
                        ? "cursor-not-allowed bg-slate-400"
                        : "bg-[#24c3be] hover:bg-[#1faeaa]"
                    }`}
                  >
                    {addingCollectionPhotos
                      ? "Adding..."
                      : `Add to ${collectionName}`}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setFavSelectorOpen(false);
                    setSelectedSpeciesForFav(null);
                    setPendingFavoriteUrl(null);
                  }}
                  className="rounded-full bg-[#374049] px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-black transition"
                >
                  Close
                </button>
              </div>
            </div>
            {selectorMode === "alpha" ? (
              <div className="mb-6 flex flex-wrap justify-center gap-1.5">
                {" "}
                {LETTERS.map((letter) => (
                  <button
                    key={letter}
                    type="button"
                    onClick={() => {
                      setSelectorLetter(letter);
                      setSelectedSpeciesForFav(null);
                      setPendingFavoriteUrl(null);
                    }}
                    className={`w-8 h-8 rounded-full text-xs font-medium transition ${
                      selectorLetter === letter
                        ? "bg-[#24c3be] text-white shadow-sm"
                        : "bg-white text-[#374049] border border-[#93ca87]/40 hover:bg-[#24c3be]/10 hover:text-[#24c3be]"
                    }`}
                  >
                    {letter}
                  </button>
                ))}
              </div>
            ) : selectorMode === "date" ? (
              <div className="mb-6 lg:float-left lg:sticky lg:top-6 lg:z-30 lg:mr-6 lg:w-80 rounded-2xl bg-white border border-[#93ca87]/40 p-3 shadow-sm">
                {" "}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="mb-4 flex flex-wrap justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowNoDatePhotos(true);
                          setSelectedSpeciesForFav(null);
                          setPendingFavoriteUrl(null);
                        }}
                        className={`rounded-full border px-4 py-2 text-xs font-medium shadow-sm transition ${
                          showNoDatePhotos
                            ? "bg-[#24c3be] border-[#24c3be] text-white"
                            : "bg-white border-[#93ca87]/40 text-[#374049] hover:bg-[#24c3be]/10 hover:text-[#24c3be]"
                        }`}
                      >
                        No date
                      </button>

                      <select
                        value={calendarYear || ""}
                        onChange={(e) => jumpToYear(Number(e.target.value))}
                        disabled={availableFavoriteYears.length === 0}
                        className="rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                      >
                        <option value="" disabled>
                          Year
                        </option>

                        {availableFavoriteYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>

                      <select
                        value={calendarMonth ?? ""}
                        onChange={(e) => jumpToMonth(Number(e.target.value))}
                        disabled={!calendarYear}
                        className="rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                      >
                        <option value="" disabled>
                          Month
                        </option>

                        {MONTH_NAMES.map((monthName, month) => {
                          const hasPhotos = (
                            availableFavoriteMonthsByYear[calendarYear] || []
                          ).includes(month);

                          return (
                            <option
                              key={month}
                              value={month}
                              disabled={!hasPhotos}
                            >
                              {monthName}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {calendarYear !== null && calendarMonth !== null ? (
                      <div className="mx-auto max-w-xs rounded-[1.5rem] border border-[#93ca87]/40 bg-[#f4f6f8] p-3">
                        {" "}
                        <div className="mb-3 flex items-center justify-between gap-2">
                          {" "}
                          <button
                            type="button"
                            onClick={() => moveToAvailableDate(-1)}
                            disabled={
                              availableFavoriteDates.indexOf(
                                selectedFavoriteDate,
                              ) <= 0 && !showNoDatePhotos
                            }
                            className="rounded-full bg-white border border-[#93ca87]/40 px-2.5 py-1.5 text-[11px] font-medium text-[#374049] hover:bg-[#24c3be]/10 disabled:opacity-40 disabled:hover:bg-white transition"
                          >
                            ‹ Previous date
                          </button>
                          <div className="w-6" />
                          <button
                            type="button"
                            onClick={() => moveToAvailableDate(1)}
                            disabled={
                              availableFavoriteDates.indexOf(
                                selectedFavoriteDate,
                              ) >=
                                availableFavoriteDates.length - 1 &&
                              !showNoDatePhotos
                            }
                            className="rounded-full bg-white border border-[#93ca87]/40 px-2.5 py-1.5 text-[11px] font-medium text-[#374049] hover:bg-[#24c3be]/10 disabled:opacity-40 disabled:hover:bg-white transition"
                          >
                            Next date ›
                          </button>
                        </div>
                        <div className="grid grid-cols-7 gap-1.5 text-center">
                          {WEEK_DAYS.map((day) => (
                            <div
                              key={day}
                              className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
                            >
                              {day}
                            </div>
                          ))}

                          {calendarCells.map((dateKey, index) => {
                            if (!dateKey) {
                              return (
                                <div
                                  key={`blank-${index}`}
                                  className="aspect-square"
                                />
                              );
                            }

                            const date = parseDateKey(dateKey);
                            const count = favoriteDateCounts[dateKey] || 0;
                            const isSelectable = count > 0;
                            const isSelected =
                              dateKey === selectedFavoriteDate &&
                              !showNoDatePhotos;

                            return (
                              <button
                                key={dateKey}
                                type="button"
                                disabled={!isSelectable}
                                onClick={() => selectCalendarDate(dateKey)}
                                className={`aspect-square rounded-xl border text-[11px] transition flex flex-col items-center justify-center ${
                                  isSelected
                                    ? "bg-[#24c3be] border-[#24c3be] text-white shadow-sm"
                                    : isSelectable
                                      ? "bg-white border-[#93ca87]/40 text-[#374049] hover:bg-[#24c3be]/10 hover:text-[#24c3be]"
                                      : "bg-white/50 border-transparent text-slate-300 cursor-not-allowed"
                                }`}
                              >
                                <span className="text-xs font-semibold">
                                  {" "}
                                  {date.getDate()}
                                </span>

                                {isSelectable && (
                                  <span
                                    className={`mt-0.5 text-[10px] ${
                                      isSelected
                                        ? "text-white/80"
                                        : "text-slate-500"
                                    }`}
                                  >
                                    {count}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl bg-white border border-[#93ca87]/40 p-8 text-center text-sm text-slate-500">
                        No dated photos available.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6 lg:float-left lg:sticky lg:top-6 lg:z-30 lg:mr-6 lg:w-80 rounded-2xl bg-white border border-[#93ca87]/40 p-3 shadow-sm">
                <div className="flex flex-col gap-4">
                  <div className="mb-1 flex flex-wrap justify-center gap-2">
                    <select
                      value={selectedFavoriteCountry}
                      onChange={(e) => selectFavoriteCountry(e.target.value)}
                      disabled={favoriteLocationCountries.length === 0}
                      className="rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                    >
                      <option value="" disabled>
                        Country
                      </option>

                      {favoriteLocationCountries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>

                    <select
                      value={selectedFavoriteAdminArea}
                      onChange={(e) => {
                        setSelectedFavoriteAdminArea(e.target.value);
                        setSelectedSpeciesForFav(null);
                        setPendingFavoriteUrl(null);
                        setPreviewPhotoBySpecies({});
                        setPreviewSlideBySpecies({});
                      }}
                      disabled={!selectedFavoriteCountry}
                      className="rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24c3be]/25"
                    >
                      <option value="" disabled>
                        State/Department
                      </option>

                      {(
                        favoriteLocationAdminAreasByCountry[
                          selectedFavoriteCountry
                        ] || []
                      ).map((adminArea) => (
                        <option key={adminArea} value={adminArea}>
                          {adminArea}
                        </option>
                      ))}
                    </select>
                  </div>

                  {favoriteLocationPoints.length > 0 ? (
                    <>
                      <LocationWorldMap
                        points={favoriteLocationPoints}
                        selectedCountry={selectedFavoriteCountry}
                      />
                      <div className="text-center text-xs text-slate-500">
                        {favoriteLocationPoints.length} mapped photo
                        {favoriteLocationPoints.length === 1 ? "" : "s"}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-2xl bg-white border border-[#93ca87]/40 p-8 text-center text-sm text-slate-500">
                      No mapped photos available.
                    </div>
                  )}
                </div>
              </div>
            )}
            <div
              className={
                selectorMode === "date" || selectorMode === "location"
                  ? "lg:min-h-[620px]"
                  : ""
              }
            >
              {!selectedSpeciesForFav ? (
                filteredSpeciesForFav.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-[#93ca87]/40 p-10 text-center text-sm text-slate-500">
                    No species found under {selectorLetter}.
                  </div>
                ) : (
                  <div
                    className={`grid gap-6 sm:gap-8 mx-auto grid-cols-1 sm:grid-cols-2 justify-items-center ${
                      selectorMode === "date" || selectorMode === "location"
                        ? "lg:grid-cols-2 xl:grid-cols-3"
                        : "lg:grid-cols-4"
                    }`}
                  >
                    {" "}
                    {filteredSpeciesForFav.map((sp) => {
                      const forms = Array.isArray(sp.forms) ? sp.forms : [];
                      const defaultForm = getDefaultForm(forms);
                      const isSinglePhotoResult =
                        selectorMode === "date" || selectorMode === "location";
                      const activeIndex = isSinglePhotoResult
                        ? 0
                        : (previewPhotoBySpecies[sp.id] ?? 0);
                      const activeForm = forms[activeIndex] || defaultForm;
                      const cardImg = activeForm?.url;
                      const photoLocation = getPhotoLocationLabel(activeForm);
                      const hasMultiplePhotos =
                        !isSinglePhotoResult && forms.length > 1;
                      const slideState = previewSlideBySpecies[sp.id];
                      const previousForm =
                        slideState && forms[slideState.from]
                          ? forms[slideState.from]
                          : null;

                      return (
                        <div
                          key={sp.id}
                          onClick={() => {
                            if (!cardImg) return;

                            if (collectionPickerMode) {
                              if (isPhotoAlreadyInCollection(cardImg)) return;

                              onToggleCollectionPhoto?.({
                                speciesId: sp.id,
                                speciesName: sp.name || "",
                                commonName: sp.common || "",
                                form: activeForm,
                              });
                              return;
                            }

                            setPendingFavoriteUrl(cardImg);
                          }}
                          className={`relative bg-white rounded-b-2xl border shadow-sm transition w-full max-w-64 flex flex-col items-center text-center overflow-visible ${
                            collectionPickerMode &&
                            isPhotoAlreadyInCollection(cardImg)
                              ? "z-0 cursor-default border-[#374049]/25 opacity-65"
                              : collectionPickerMode &&
                                  isCollectionPhotoSelected(cardImg)
                                ? "z-20 cursor-pointer border-[#24c3be] ring-4 ring-[#24c3be]/25 shadow-md"
                                : !collectionPickerMode &&
                                    pendingFavoriteUrl === cardImg
                                  ? "z-50 cursor-pointer border-[#24c3be] ring-4 ring-[#24c3be]/25"
                                  : !collectionPickerMode && pendingFavoriteUrl
                                    ? "z-0 cursor-pointer border-[#93CA87]/40 opacity-35 grayscale pointer-events-none"
                                    : "z-0 cursor-pointer border-[#93CA87]/40 hover:shadow-md hover:-translate-y-[2px]"
                          }`}
                        >
                          <div className="relative w-full aspect-[4/3] flex items-center justify-center bg-white">
                            {collectionPickerMode &&
                            isPhotoAlreadyInCollection(cardImg) ? (
                              <div className="absolute right-3 top-3 z-40 rounded-full border border-white/80 bg-[#374049] px-2.5 py-1 text-[10px] font-medium text-white shadow-md">
                                In collection
                              </div>
                            ) : (
                              collectionPickerMode &&
                              isCollectionPhotoSelected(cardImg) && (
                                <div className="absolute right-3 top-3 z-40 flex h-7 w-7 items-center justify-center rounded-full border border-white bg-[#24c3be] text-sm font-semibold text-white shadow-md">
                                  ✓
                                </div>
                              )
                            )}

                            {cardImg ? (
                              <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
                                {slideState && previousForm && (
                                  <img
                                    src={previousForm.url}
                                    alt=""
                                    className={`absolute max-w-full max-h-full object-contain transition-all duration-300 ease-out ${
                                      slideState.direction === "right"
                                        ? "-translate-x-full opacity-0"
                                        : "translate-x-full opacity-0"
                                    }`}
                                  />
                                )}

                                <img
                                  key={cardImg}
                                  src={cardImg}
                                  alt={sp.name || "Species"}
                                  loading="lazy"
                                  className={`absolute max-w-full max-h-full object-contain transition-all duration-300 ease-out ${
                                    slideState
                                      ? slideState.direction === "right"
                                        ? "translate-x-0 animate-[slideFromRight_300ms_ease-out]"
                                        : "translate-x-0 animate-[slideFromLeft_300ms_ease-out]"
                                      : ""
                                  }`}
                                />
                              </div>
                            ) : (
                              <div className="text-sm text-slate-400">
                                No photo
                              </div>
                            )}

                            {hasMultiplePhotos && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    slidePreviewPhoto(
                                      sp.id,
                                      activeIndex === 0
                                        ? forms.length - 1
                                        : activeIndex - 1,
                                      "left",
                                      forms[
                                        activeIndex === 0
                                          ? forms.length - 1
                                          : activeIndex - 1
                                      ]?.url,
                                    );
                                  }}
                                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/20 text-white backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-black/35 active:scale-95 transition"
                                >
                                  ‹
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    slidePreviewPhoto(
                                      sp.id,
                                      activeIndex === forms.length - 1
                                        ? 0
                                        : activeIndex + 1,
                                      "right",
                                      forms[
                                        activeIndex === forms.length - 1
                                          ? 0
                                          : activeIndex + 1
                                      ]?.url,
                                    );
                                  }}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-black/20 text-white backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-black/35 active:scale-95 transition"
                                >
                                  ›
                                </button>
                              </>
                            )}
                            {hasMultiplePhotos && (
                              <div
                                className="absolute left-2 bottom-2 z-20 flex justify-start gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {forms.map((form, photoIndex) => (
                                  <button
                                    key={form.url || photoIndex}
                                    type="button"
                                    onClick={() =>
                                      slidePreviewPhoto(
                                        sp.id,
                                        photoIndex,
                                        photoIndex > activeIndex
                                          ? "right"
                                          : "left",
                                        form.url,
                                      )
                                    }
                                    className={`w-5 h-5 rounded overflow-hidden border bg-white/80 backdrop-blur-sm transition ${
                                      photoIndex === activeIndex
                                        ? "border-[#24c3be] ring-2 ring-[#24c3be]/30"
                                        : "border-white/70 hover:border-[#24c3be]/70"
                                    }`}
                                  >
                                    <img
                                      src={form.url}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="pt-3 pb-5 px-3">
                            {selectorMode === "alpha" &&
                            selectorSort === "common" ? (
                              <>
                                <h3
                                  className="text-xl text-[#374049]"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                >
                                  {sp.common || "Unknown Common Name"}
                                </h3>

                                <p
                                  className="text-[#374049]/60 text-sm italic"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                >
                                  {sp.name || "—"}
                                </p>
                              </>
                            ) : (
                              <>
                                <h3
                                  className="text-xl italic text-[#374049]"
                                  style={{ fontFamily: "Cormorant Garamond" }}
                                >
                                  {sp.name || "—"}
                                </h3>

                                <p className="text-[#374049]/60 text-sm">
                                  {sp.common || ""}
                                </p>
                              </>
                            )}

                            {photoLocation && (
                              <p className="mt-1 text-xs text-slate-500 truncate max-w-52">
                                ⌖ {photoLocation}
                              </p>
                            )}
                          </div>

                          {!collectionPickerMode &&
                            pendingFavoriteUrl === cardImg && (
                              <div className="absolute left-1/2 top-full z-[60] flex -translate-x-1/2 flex-col items-center">
                                <div className="h-8 w-[2px] bg-[#24c3be]" />

                                <div className="w-72 rounded-2xl bg-white border border-[#93ca87]/40 shadow-xl p-4 text-center">
                                  <div className="text-sm font-medium text-[#374049]">
                                    {forumPhotoPickerMode
                                      ? "Use this photo for the topic?"
                                      : "Use this photo as a personal highlight?"}
                                  </div>

                                  <div className="text-xs text-slate-500 mt-1">
                                    {forumPhotoPickerMode
                                      ? "The original photo will remain in your BioIndex collection."
                                      : "This will update the selected highlight slot on your profile."}
                                  </div>

                                  <div className="mt-4 flex justify-center gap-2">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPendingFavoriteUrl(null);
                                      }}
                                      className="rounded-full bg-white border border-[#93ca87]/40 px-4 py-2 text-xs font-medium text-[#374049] shadow-sm hover:bg-slate-50 transition"
                                    >
                                      Cancel
                                    </button>

                                    <button
                                      type="button"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (savingFavorite) return;

                                        setSavingFavorite(true);

                                        try {
                                          await assignFavorite(
                                            pendingFavoriteUrl,
                                          );
                                        } finally {
                                          setSavingFavorite(false);
                                        }
                                      }}
                                      disabled={savingFavorite}
                                      className={`rounded-full px-4 py-2 text-xs font-medium text-white shadow-sm transition ${
                                        savingFavorite
                                          ? "bg-slate-400 cursor-not-allowed"
                                          : "bg-[#24c3be] hover:bg-[#1faeaa]"
                                      }`}
                                    >
                                      {savingFavorite
                                        ? forumPhotoPickerMode
                                          ? "Selecting..."
                                          : "Saving..."
                                        : forumPhotoPickerMode
                                          ? "Use Photo"
                                          : "Confirm"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                )
              ) : null}
            </div>
          </div>
        </div>{" "}
      </>
    );
  }

  return (
    <>
      <style>
        {`
        @keyframes galleryGlint {
          from {
            transform: translateX(0) rotate(12deg);
          }
          to {
            transform: translateX(320%) rotate(12deg);
          }
        }
      `}
      </style>
      <div className="relative overflow-visible rounded-[2rem] bg-white shadow-lg border border-[#93ca87]/40 p-6 sm:p-8">
        <div className="mb-5 sm:mb-7">
          <div className="bioindex-section-heading">
            <h2
              className="text-3xl leading-none text-[#374049] sm:text-4xl"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              My Gallery
            </h2>
            <div className="bioindex-section-actions" aria-label="Gallery actions">
              <ProfileSectionAction
                kind="info"
                label="About Gallery"
                active={showGalleryHint}
                onClick={() => setShowGalleryHint((prev) => !prev)}
              />
              {isOwnProfile && (
                <ProfileSectionAction
                  kind={editingHighlights ? "check" : "edit"}
                  label={editingHighlights ? "Done Editing Gallery" : "Edit Gallery"}
                  active={editingHighlights}
                  onClick={() => setEditingHighlights((prev) => !prev)}
                />
              )}
            </div>
          </div>
          {showGalleryHint && (
            <div className="bioindex-section-hint" role="note">
              Your gallery features up to four selected photographs. To change
              a photograph, use Edit Gallery and select its position.
            </div>
          )}
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {[0, 1, 2, 3].map((index) => {
            const favoriteUrl = (profile.favorites || [])[index];

            const favoriteSpecies = speciesWithPhotos.find((sp) =>
              (sp.forms || []).some((form) => form.url === favoriteUrl),
            );

            return (
              <div
                key={index}
                onClick={() => {
                  if (!isOwnProfile) return;
                  if (!editingHighlights && favoriteUrl) return;

                  sessionStorage.setItem(
                    "bioindex_gallery_scroll_y",
                    String(window.scrollY),
                  );

                  openFavoriteSelector(index);
                  setEditingHighlights(false);
                }}
                className={`group relative overflow-hidden bg-transparent shadow-none ${
                  isOwnProfile && (editingHighlights || !favoriteUrl)
                    ? "cursor-pointer"
                    : ""
                }`}
              >
                <div className="relative w-full aspect-[4/3]">
                  <div className="absolute inset-[11%] bg-slate-100 overflow-hidden">
                    {favoriteUrl ? (
                      <img
                        src={favoriteUrl}
                        alt=""
                        onClick={() => {
                          if (editingHighlights) return;
                          openFavoriteViewer(favoriteUrl);
                        }}
                        className="w-full h-full object-cover cursor-pointer"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-center px-5 bg-[#f4f6f8]">
                        <div className="w-10 h-10 rounded-full bg-[#24c3be]/10 border border-[#24c3be]/30 flex items-center justify-center text-[#24c3be] mb-3">
                          +
                        </div>

                        <div className="text-sm text-slate-500">
                          No highlight selected
                        </div>
                      </div>
                    )}
                  </div>

                  <img
                    src={galleryFrame}
                    alt=""
                    className="pointer-events-none absolute inset-0 z-10 w-full h-full object-contain"
                  />

                  {favoriteUrl && (
                    <div className="pointer-events-none absolute inset-[11%] z-20 overflow-hidden">
                      <div className="absolute inset-0 bg-white/10 mix-blend-screen" />
                      <div className="absolute left-0 top-0 h-full w-full bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-45" />

                      {!editingHighlights && (
                        <div className="absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100">
                          <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md group-hover:animate-[galleryGlint_900ms_ease-out_1]" />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {favoriteUrl && favoriteSpecies && (
                  <div className="mx-auto mt-1.5 w-[72%] rounded-[4px] border border-[#6f6658] bg-[#2b2d36] px-2 py-1 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.18),inset_0_-1px_0_rgba(0,0,0,0.55),0_1px_2px_rgba(55,64,73,0.25)]">
                    <div className="relative border-y border-[#b7a77a]/65 px-5 py-0.5 before:absolute before:left-1 before:top-1/2 before:h-1.5 before:w-1.5 before:-translate-y-1/2 before:rotate-45 before:border before:border-[#b7a77a]/70 after:absolute after:right-1 after:top-1/2 after:h-1.5 after:w-1.5 after:-translate-y-1/2 after:rotate-45 after:border after:border-[#b7a77a]/70">
                      <div
                        className="truncate text-[11px] leading-tight tracking-wide text-[#c8b98b]"
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {favoriteSpecies.common || "Unknown Common Name"}{" "}
                        <span className="italic">
                          ({favoriteSpecies.name || "—"})
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {editingHighlights && (
                  <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/10 opacity-0 transition group-hover:opacity-100">
                    <div className="rounded-xl border border-[#24C3BE] bg-white px-4 py-2 text-xs font-semibold text-[#01767a] shadow-sm">
                      Change Highlight {index + 1}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {savedMessage && (
          <div className="text-sm text-green-600 mt-4 text-center">
            Favorites saved
          </div>
        )}
      </div>
    </>
  );
}
